import React, { useState, useEffect } from 'react';

import {
    Grid,
    Stack,
    Button,
    Container,
    Typography,
    styled,
    Card,
    Box
} from '@mui/material';

import { useNavigate } from 'react-router-dom';

import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useUsers, userTypes, targetTypes } from '../../../hooks/api/useUsers';
import { useSystemRoutes, routeTypes } from "../../../hooks/useSystemRoutes"
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';

import { icons } from '../../common/CommonIcons';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import CommonBreadcumbs from "../../common/CommonBreadcumbs"
import CommonRestricted from '../../common/CommonRestricted';

import { ownerTypes } from "../../../schema/users/memberSchema"

const StyledCard = styled(Card)(({ theme }) => ({
    // padding: theme.spacing(4)
}));

export default function ListMembers({
    routeType,
    ownerType,
    ownerId
}) {

    const { promptMessage } = useSystemPrompt();
    const { list, remove, filterOperatorTypes } = useUsers({ userType: userTypes.member, targetType: ownerType });
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.member.id)

    const navigate = useNavigate();

    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)

    const gridRef = React.createRef();

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {

            // check the owner types
            if (ownerType === ownerTypes.retailer) {

                let filterValue = []

                filterValue.push({
                    name: "owner_id",
                    operator: filterOperatorTypes.eq,
                    value: ownerId
                })

                filterValue.push({
                    name: "owner_type",
                    operator: filterOperatorTypes.eq,
                    value: ownerTypes.retailer
                })

                if (props?.filterValue) {
                    filterValue = [...props.filterValue, ...filterValue]
                }

                props.filterValue = filterValue

            }


            const result = await list(props)

            return {
                data: result?.data?.members,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    const handleDelete = async () => {

        try {
            const result = await remove({ ids: selectedIds })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

            resetDataGridTableSelection();

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    useEffect(() => {

        defineColumnsVew()

    }, [])

    const defineColumnsVew = () => {
        defineColumns([
            {
                name: 'username',
                header: 'Username',
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageUpdate.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`users/member/update/${cellProps.data.user_id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'firstname', header: 'First Name'
            },
            {
                name: 'lastname', header: 'Last Name'
            },
            {
                name: 'contact', header: 'Contact'
            },
            {
                name: 'email', header: 'Email'
            },
            {
                name: 'identity_no', header: 'Identity No.'
            },
            {
                name: 'status',
                header: 'Status',
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, ["Active", "-"], ["primary", "secondary"], "")
            }
        ])
    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("users/member/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "user_id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.members })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle="Members"
                breadcrumbs={[
                    {
                        displayText: "Members",
                        props: {
                            // underline: "hover",
                        }
                    },
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    );
}
